import { jsx as _jsx, Fragment as _Fragment } from "react/jsx-runtime";
// BUG: Known issues:
// [ ] validateDOMNesting(...) warning - incorrect table elements nesting
// [ ] scrollToIndex (react-virtualized) doesn't work - no correct row indexes after filtering or sorting
// https://github.com/mbrn/material-table/issues/891#issuecomment-681986647
// https://github.com/mbrn/material-table/issues/2382
import { memo, useCallback, useEffect, useMemo, useRef, } from 'react';
import { useResizeDetector } from 'react-resize-detector';
import MaterialTable, { MTableAction, MTableCell, MTableHeader, MTableToolbar, } from '@material-table/core';
import Tooltip from '../../components/Tooltip';
import { searchFieldDebounceInterval } from '../../utils/constants';
import { virtualizedTableCellStyle } from './const';
import VirtualizedTableBody from './VirtualizedTableBody';
import * as S from './VirtualizedTable.css';
const VirtualizedTable = (props) => {
    var _a, _b, _c;
    const currentRowHeight = props.rowHeight;
    const { height: tableHeight, ref: tableRef } = useResizeDetector();
    const tableComponentRef = useRef(MaterialTable);
    const { height: toolbarHeight, ref: toolbarRef } = useResizeDetector();
    const customColumns = useMemo(() => {
        var _a;
        return (_a = props === null || props === void 0 ? void 0 : props.columns) === null || _a === void 0 ? void 0 : _a.map((column) => {
            var _a;
            return (Object.assign(Object.assign({}, column), { sorting: (_a = column.sorting) !== null && _a !== void 0 ? _a : false, 
                // to disable Material-Table default filtering (show all data from backend)
                customFilterAndSearch: props.disableDefaultSearchAndFilter
                    ? () => true
                    : column.customFilterAndSearch }));
        });
    }, [props.columns, props.disableDefaultSearchAndFilter]);
    useEffect(() => {
        var _a, _b, _c, _d;
        if (((_b = (_a = tableComponentRef === null || tableComponentRef === void 0 ? void 0 : tableComponentRef.current) === null || _a === void 0 ? void 0 : _a.state) === null || _b === void 0 ? void 0 : _b.data) &&
            props.onFilteredDataChanged) {
            props.onFilteredDataChanged((_d = (_c = tableComponentRef === null || tableComponentRef === void 0 ? void 0 : tableComponentRef.current) === null || _c === void 0 ? void 0 : _c.state) === null || _d === void 0 ? void 0 : _d.data);
        }
    }, [props, props.onFilteredDataChanged]);
    const tableStyle = useMemo(() => ({
        overflow: 'auto',
        border: 0,
        boxShadow: 'none',
    }), []);
    const tableOptions = useMemo(() => {
        var _a, _b;
        return (Object.assign({ debounceInterval: searchFieldDebounceInterval, actionsColumnIndex: -1, toolbar: !!((_a = props.components) === null || _a === void 0 ? void 0 : _a.Toolbar), paging: false, sorting: true, draggable: false, minBodyHeight: props.bodyHeight, overflowY: 'hidden', maxBodyHeight: props.bodyHeight, headerStyle: {
                textTransform: 'capitalize',
            }, actionsCellStyle: Object.assign({}, (props.actionsColumnWidth && { width: props.actionsColumnWidth })), tableLayout: ((_b = props.options) === null || _b === void 0 ? void 0 : _b.filtering) ? 'fixed' : 'auto' }, props.options));
    }, [
        props.actionsColumnWidth,
        props.bodyHeight,
        (_a = props.components) === null || _a === void 0 ? void 0 : _a.Toolbar,
        props.options,
    ]);
    const cellTooltipMessage = useCallback((cellProps) => {
        if (props.disableTooltips || typeof cellProps.value !== 'string') {
            return '';
        }
        else {
            return cellProps.value;
        }
    }, [props.disableTooltips]);
    const BodyComponent = useCallback((innerProps) => (_jsx(VirtualizedTableBody, Object.assign({ customTableHeight: tableHeight, customRowHeight: currentRowHeight, customToolbarHeight: toolbarHeight }, innerProps))), [currentRowHeight, tableHeight, toolbarHeight]);
    const HeaderComponent = useCallback((innerProps) => {
        var _a;
        return (_jsx(S.TableHeaderWrapper, Object.assign({ filtering: (_a = innerProps === null || innerProps === void 0 ? void 0 : innerProps.options) === null || _a === void 0 ? void 0 : _a.filtering, actionsColumnWidth: props.actionsColumnWidth }, { children: _jsx(MTableHeader, Object.assign({}, innerProps)) })));
    }, [props.actionsColumnWidth]);
    const CellComponent = useCallback((innerProps) => {
        var _a;
        return (_jsx(Tooltip, Object.assign({ title: cellTooltipMessage(innerProps) }, { children: ((_a = props.components) === null || _a === void 0 ? void 0 : _a.Cell) ? (_jsx(props.components.Cell, Object.assign({}, innerProps, { style: virtualizedTableCellStyle }))) : (_jsx(MTableCell, Object.assign({}, innerProps, { style: virtualizedTableCellStyle }))) })));
    }, [cellTooltipMessage, props]);
    const ActionComponent = useCallback((innerProps) => {
        var _a;
        return (_jsx(S.TableActionsColumn, { children: ((_a = props.components) === null || _a === void 0 ? void 0 : _a.Action) ? (_jsx(props.components.Action, Object.assign({}, innerProps))) : (_jsx(MTableAction, Object.assign({}, innerProps))) }));
    }, 
    // optimization
    // fixes a wrong ignore dialog position in Dashboards/Alerts
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [props.components]);
    const ToolbarComponent = useCallback((innerProps) => {
        var _a;
        return (_jsx("div", Object.assign({ ref: toolbarRef }, { children: ((_a = props.components) === null || _a === void 0 ? void 0 : _a.Toolbar) ? (_jsx(props.components.Toolbar, Object.assign({}, innerProps))) : (_jsx(MTableToolbar, Object.assign({}, innerProps))) })));
    }, 
    // optimization - experimentally
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [props.components, toolbarRef]);
    const tableComponents = useMemo(() => ({
        Body: BodyComponent,
        Header: HeaderComponent,
        Cell: CellComponent,
        Action: ActionComponent,
        Toolbar: ToolbarComponent,
    }), [
        ActionComponent,
        BodyComponent,
        CellComponent,
        HeaderComponent,
        ToolbarComponent,
    ]);
    const hiddenActionsHeader = useMemo(() => ({
        header: {
            actions: '',
        },
        body: {
            emptyDataSourceMessage: 'No records to display',
        },
    }), []);
    const hiddenSortArrows = useMemo(() => ({
        SortArrow: () => _jsx(_Fragment, {}),
    }), []);
    useEffect(() => {
        if (props.setFirstRow) {
            !props.isLoading &&
                setTimeout(() => {
                    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o;
                    (_o = (_m = (_l = (_k = (_j = (_h = (_g = (_f = (_e = (_d = (_c = (_b = (_a = tableRef === null || tableRef === void 0 ? void 0 : tableRef.current) === null || _a === void 0 ? void 0 : _a.children[0]) === null || _b === void 0 ? void 0 : _b.children[0]) === null || _c === void 0 ? void 0 : _c.children[0]) === null || _d === void 0 ? void 0 : _d.children[0]) === null || _e === void 0 ? void 0 : _e.children[0]) === null || _f === void 0 ? void 0 : _f.children[0]) === null || _g === void 0 ? void 0 : _g.children[1]) === null || _h === void 0 ? void 0 : _h.children[1]) === null || _j === void 0 ? void 0 : _j.children[0]) === null || _k === void 0 ? void 0 : _k.children[0]) === null || _l === void 0 ? void 0 : _l.children[0]) === null || _m === void 0 ? void 0 : _m.children[0]) === null || _o === void 0 ? void 0 : _o.click();
                }, 0);
        }
    }, [props.isLoading, props.setFirstRow, tableRef]);
    return (_jsx("div", Object.assign({ ref: tableRef, className: ((_b = props.options) === null || _b === void 0 ? void 0 : _b.filtering) ? 'material-table-with-filters' : '' }, { children: _jsx(MaterialTable, { tableRef: tableComponentRef, title: (_c = props.title) !== null && _c !== void 0 ? _c : '', style: tableStyle, columns: customColumns, data: props.data, isLoading: props.isLoading, localization: Object.assign(Object.assign({}, hiddenActionsHeader), props.localization), options: tableOptions, actions: props.actions, icons: Object.assign(Object.assign({}, (!props.showSortingArrows && hiddenSortArrows)), props.icons), onRowClick: props.onRowClick, components: tableComponents, onFilterChange: props.onFilterChange }) })));
};
export default memo(VirtualizedTable);
